/* Canonical ZHELTA Academy programs catalog.
 *
 * Slugs match the backend (backend/academy_catalog.py) so enrollments,
 * checkout and /academy/courses/{slug} all line up. Content is self-hosted;
 * no external dependencies. Images served from Unsplash CDN (free, no key). */
export const PROGRAMS = [
  {
    slug: 'it-certification-programs',
    title: 'IT Certification Programs',
    tagline: 'Industry-recognized credentials to kickstart your IT career.',
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1600&q=70',
    icon: 'Award',
    gradient: 'from-amber-400 to-orange-500',
    duration: '12–24 weeks',
    level: 'Beginner → Intermediate',
    audience: 'Career-changers, IT support hopefuls, recent graduates',
    summary:
      'Prepare for globally recognised IT certifications including CompTIA A+, Network+, Security+, AWS Cloud Practitioner and Microsoft Azure Fundamentals. Every track pairs exam prep with hands-on labs.',
    whatYouLearn: [
      'Hardware, networking and OS fundamentals (A+ / Network+)',
      'Security foundations and vulnerability management (Security+)',
      'Cloud service models, deployment and billing (AWS / Azure)',
      'Exam strategy, timed practice tests and lab recordings',
      'Portfolio-ready write-ups for recruiter conversations',
    ],
    outcomes: [
      'Pass at least one vendor certification exam',
      'Qualify for IT Support, Help Desk, or Junior Cloud roles',
      'Build a LinkedIn-ready project portfolio',
    ],
    syllabus: [
      { week: '1–4', title: 'CompTIA A+ Core Concepts',    body: 'Hardware, mobile devices, networking basics, virtualisation.' },
      { week: '5–8', title: 'Networking & Security+',       body: 'TCP/IP, routing, firewalls, cryptography, incident response.' },
      { week: '9–14', title: 'Cloud Foundations',           body: 'AWS + Azure core services, IAM, pricing, monitoring.' },
      { week: '15–18', title: 'Labs & Mock Exams',          body: 'Guided hands-on labs + proctored mock exams.' },
      { week: '19–24', title: 'Certification Launchpad',   body: 'Scheduling real exams, resume + interview prep.' },
    ],
  },
  {
    slug: 'computer-hardware-system-fundamentals',
    title: 'Computer Hardware & System Fundamentals',
    tagline: 'Build, troubleshoot, and repair PCs from the ground up.',
    image: 'https://images.unsplash.com/photo-1555680202-c86f0e12f086?auto=format&fit=crop&w=1600&q=70',
    icon: 'Cpu',
    gradient: 'from-slate-600 to-slate-800',
    duration: '10 weeks',
    level: 'Beginner',
    audience: 'Aspiring PC technicians and IT Support Assistants',
    summary:
      'Master computer hardware components, system architecture and troubleshooting techniques. Hands-on labs on real hardware and virtualised environments.',
    whatYouLearn: [
      'CPU, memory, storage and motherboard architecture',
      'Assembling, diagnosing, and repairing desktop/laptop systems',
      'Operating-system installation and disk partitioning',
      'Peripherals, cabling, connectors and power',
      'Thermal management, BIOS/UEFI and firmware updates',
    ],
    outcomes: [
      'Build and configure a working PC from components',
      'Diagnose and fix 90% of common hardware failures',
      'Qualify for PC Technician and IT Support Assistant roles',
    ],
    syllabus: [
      { week: '1', title: 'Inside the PC',            body: 'Component tour, safety, ESD, toolkit.' },
      { week: '2–3', title: 'CPU & Motherboard',      body: 'Sockets, chipsets, memory channels.' },
      { week: '4–5', title: 'Storage & I/O',          body: 'HDD/SSD, SATA/NVMe, RAID basics.' },
      { week: '6–7', title: 'Assembly Lab',            body: 'Full build from parts; first boot diagnostics.' },
      { week: '8–10', title: 'Troubleshooting Clinic', body: 'Fault-finding, BIOS recovery, OS repair.' },
    ],
  },
  {
    slug: 'software-installation-use-support',
    title: 'Software Installation, Use & Support',
    tagline: 'Support end-users across Windows, macOS and Linux.',
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1600&q=70',
    icon: 'MonitorCog',
    gradient: 'from-sky-500 to-indigo-600',
    duration: '8 weeks',
    level: 'Beginner',
    audience: 'Future Help Desk and Support Technicians',
    summary:
      'Learn to install, configure, support and update software across multiple operating systems. Ticketing workflows, remote support and customer communication included.',
    whatYouLearn: [
      'Deploying Windows / macOS / Linux for end-users',
      'Installing, updating and uninstalling applications',
      'Remote support tools, screen-share and diagnostics',
      'Ticketing systems (Jira, Zendesk, Freshservice)',
      'Writing clear, empathetic support responses',
    ],
    outcomes: [
      'Resolve tickets confidently across three OS platforms',
      'Qualify for Help Desk, Support Technician and IT Assistant roles',
    ],
    syllabus: [
      { week: '1–2', title: 'OS Installation & Admin', body: 'User accounts, drivers, updates.' },
      { week: '3–4', title: 'Software Lifecycle',       body: 'Packages, app stores, scripts.' },
      { week: '5–6', title: 'Remote Support Lab',       body: 'Live support scenarios on real machines.' },
      { week: '7–8', title: 'Ticketing & Comms',        body: 'SLA basics, escalation, documentation.' },
    ],
  },
  {
    slug: 'cybersecurity-awareness-best-practices',
    title: 'Cybersecurity Awareness & Best Practices',
    tagline: 'Defend systems, data and users against modern threats.',
    image: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?auto=format&fit=crop&w=1600&q=70',
    icon: 'ShieldCheck',
    gradient: 'from-emerald-500 to-teal-600',
    duration: '12 weeks',
    level: 'Beginner → Intermediate',
    audience: 'Aspiring Cybersecurity Support and Compliance analysts',
    summary:
      'Develop the essential cybersecurity skills used by frontline defenders: identifying threats, hardening systems, safe browsing, password hygiene, phishing response and entry-level incident response.',
    whatYouLearn: [
      'Threat landscape and attacker mindset',
      'Phishing analysis, MFA and password managers',
      'Endpoint protection and patch management',
      'Incident response playbooks and triage',
      'Compliance basics (ISO 27001, NIST CSF, GDPR)',
    ],
    outcomes: [
      'Identify and report phishing, social engineering and malware',
      'Write a simple incident report and runbook',
      'Qualify for Cybersecurity Awareness and Compliance-Support roles',
    ],
    syllabus: [
      { week: '1–2', title: 'Threat Landscape',        body: 'Attack types, actors, motivations.' },
      { week: '3–4', title: 'Hardening Fundamentals',   body: 'OS hardening, patching, least privilege.' },
      { week: '5–6', title: 'Phishing & Social Eng.',   body: 'Detection, analysis, user training.' },
      { week: '7–9', title: 'Incident Response Lab',    body: 'Triage tabletop and real log analysis.' },
      { week: '10–12', title: 'Compliance Primer',       body: 'Controls mapping, policies, evidence.' },
    ],
  },
  {
    slug: 'programming-software-development',
    title: 'Programming & Software Development',
    tagline: 'Ship real applications with Python, JavaScript, HTML/CSS and Git.',
    image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=1600&q=70',
    icon: 'Code2',
    gradient: 'from-violet-500 to-fuchsia-600',
    duration: '16 weeks',
    level: 'Beginner → Intermediate',
    audience: 'Aspiring developers and career-changers into tech',
    summary:
      'Build a portfolio of real applications using Python, HTML, JavaScript and databases while learning version control (Git/GitHub) to start your developer career.',
    whatYouLearn: [
      'Python fundamentals, functions, modules and testing',
      'HTML, CSS, modern JavaScript (ES2024+)',
      'Relational databases and SQL basics',
      'Git, branches, pull requests and code review',
      'Shipping a final capstone app end-to-end',
    ],
    outcomes: [
      'Deploy 3 projects to the public internet',
      'Open, review and merge pull requests confidently',
      'Qualify for Junior Developer, QA Automation or Data Engineer internships',
    ],
    syllabus: [
      { week: '1–4',  title: 'Python Core',         body: 'Syntax, data, functions, small scripts.' },
      { week: '5–7',  title: 'Web Foundations',      body: 'HTML/CSS/JS, forms, DOM.' },
      { week: '8–10', title: 'SQL & Data',           body: 'Schema design, queries, joins.' },
      { week: '11–13', title: 'Git & Collaboration', body: 'Branches, PRs, code review etiquette.' },
      { week: '14–16', title: 'Capstone Project',     body: 'Build & deploy a full-stack app.' },
    ],
  },
  {
    slug: 'mathematics-logical-thinking-it',
    title: 'Mathematics & Logical Thinking for IT',
    tagline: 'Sharpen the reasoning that powers every great engineer.',
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&w=1600&q=70',
    icon: 'Brain',
    gradient: 'from-rose-500 to-pink-600',
    duration: '10 weeks',
    level: 'Beginner → Intermediate',
    audience: 'Students preparing for data, programming or analytics tracks',
    summary:
      'Develop the mathematical and logical problem-solving skills that underpin data science, programming and tech analytics careers.',
    whatYouLearn: [
      'Discrete math: sets, logic, combinatorics',
      'Algebra refresher and linear algebra intro',
      'Statistics & probability for engineers',
      'Algorithmic thinking and pseudocode',
      'Daily problem-solving drills',
    ],
    outcomes: [
      'Read and write formal reasoning about code',
      'Solve entry-level algorithm problems from scratch',
    ],
    syllabus: [
      { week: '1–2', title: 'Logic & Sets',     body: 'Propositional logic, truth tables.' },
      { week: '3–5', title: 'Algebra Refresher', body: 'Functions, matrices, vectors.' },
      { week: '6–7', title: 'Stats & Probability', body: 'Distributions, expectation.' },
      { week: '8–10', title: 'Algorithmic Thinking', body: 'Pseudocode, complexity, drills.' },
    ],
  },
  {
    slug: 'creative-technology-media-literacy',
    title: 'Creative Technology & Media Literacy',
    tagline: 'Design and create for the modern web.',
    image: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=1600&q=70',
    icon: 'Palette',
    gradient: 'from-pink-500 to-rose-600',
    duration: '8 weeks',
    level: 'Beginner',
    audience: 'Designers, marketers and content creators',
    summary:
      'Design visual content using modern tools like Canva and Figma while learning UX/UI principles, visual storytelling and responsible media consumption.',
    whatYouLearn: [
      'Visual design and layout principles',
      'Figma: components, prototyping, auto-layout',
      'Canva for marketing deliverables',
      'Typography, colour theory and accessibility',
      'Media literacy and ethical content creation',
    ],
    outcomes: [
      'Ship a complete visual identity for a real client brief',
      'Build a Figma prototype recruiters can click through',
    ],
    syllabus: [
      { week: '1–2', title: 'Design Fundamentals', body: 'Layout, hierarchy, grids.' },
      { week: '3–4', title: 'Figma Deep Dive',      body: 'Components, auto-layout, plugins.' },
      { week: '5–6', title: 'Canva Rapid Delivery', body: 'Social, ads, decks.' },
      { week: '7–8', title: 'Client Brief Capstone', body: 'Ship a real-world design package.' },
    ],
  },
  {
    slug: 'digital-citizenship-civic-education',
    title: 'Digital Citizenship & Civic Education',
    tagline: 'Use technology responsibly, ethically and with impact.',
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=70',
    icon: 'Vote',
    gradient: 'from-cyan-500 to-blue-600',
    duration: '6 weeks',
    level: 'Beginner',
    audience: 'Students, young professionals and educators',
    summary:
      'Learn to use technology responsibly and ethically while protecting against misinformation and preparing for digital advocacy and civic-engagement roles.',
    whatYouLearn: [
      'Online identity, privacy and digital wellbeing',
      'Spotting misinformation and deepfakes',
      'Ethics in AI and automation',
      'Civic engagement through technology',
      'Communicating responsibly on social platforms',
    ],
    outcomes: [
      'Audit and harden your own digital footprint',
      'Lead a digital-literacy session for peers',
    ],
    syllabus: [
      { week: '1', title: 'Your Digital Identity', body: 'Privacy, breaches, hygiene.' },
      { week: '2–3', title: 'Misinformation Lab',    body: 'Spotting, sourcing, fact-checking.' },
      { week: '4', title: 'AI Ethics',               body: 'Bias, automation, accountability.' },
      { week: '5–6', title: 'Civic Tech Capstone',  body: 'Lead a community awareness session.' },
    ],
  },
  {
    slug: 'professional-communication-it-documentation',
    title: 'Professional Communication & IT Documentation',
    tagline: 'Write, speak and document like a senior engineer.',
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1600&q=70',
    icon: 'MessageCircle',
    gradient: 'from-orange-400 to-red-500',
    duration: '8 weeks',
    level: 'Beginner → Intermediate',
    audience: 'IT administrators, PMs, and engineers who write',
    summary:
      'Master professional written and spoken communication and technical documentation skills for IT administrative, support and project-coordination roles.',
    whatYouLearn: [
      'Business writing and tone for IT contexts',
      'Writing effective runbooks, SOPs and post-mortems',
      'Technical presentations and stakeholder updates',
      'Meeting facilitation and async collaboration',
      'Documentation-as-code (Markdown, Notion, Confluence)',
    ],
    outcomes: [
      'Publish a polished technical runbook and post-mortem',
      'Run a stakeholder update that executives actually read',
    ],
    syllabus: [
      { week: '1–2', title: 'Business Writing',     body: 'Email, brief memos, tone.' },
      { week: '3–4', title: 'Technical Docs',        body: 'SOPs, runbooks, architecture notes.' },
      { week: '5–6', title: 'Presentation Craft',    body: 'Slides, storytelling, Q&A.' },
      { week: '7–8', title: 'Async Collaboration',   body: 'PR descriptions, Slack etiquette.' },
    ],
  },
  {
    slug: 'role-based-learning-portals',
    title: 'Role-Based Learning Pathways',
    tagline: 'Career-ready pathways built around specific IT roles.',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1600&q=70',
    icon: 'Briefcase',
    gradient: 'from-stone-600 to-stone-800',
    duration: '16–24 weeks',
    level: 'Intermediate',
    audience: 'Job-seekers targeting a specific IT role',
    summary:
      'Complete a specialised career pathway with portfolio development to become job-ready for a specific IT role: Data Analyst, Web Developer, Cloud Support, Cybersecurity Analyst and more.',
    whatYouLearn: [
      'Role-specific stack and tooling',
      'End-to-end portfolio project for your target role',
      'Interview prep (behavioural + technical)',
      'Resume, LinkedIn and recruiter outreach',
      'Mock interviews with industry mentors',
    ],
    outcomes: [
      'Graduate with a complete, recruiter-ready portfolio',
      'Coached through at least three real interview loops',
    ],
    syllabus: [
      { week: '1–4',   title: 'Role Deep Dive',        body: 'What senior folks do day-to-day.' },
      { week: '5–14',  title: 'Capstone Portfolio',   body: 'Ship your signature project.' },
      { week: '15–20', title: 'Interview Gauntlet',   body: 'Weekly mocks with feedback.' },
      { week: '21–24', title: 'Job Search Launchpad', body: 'Outreach, referrals, offer coaching.' },
    ],
  },
  {
    slug: 'learning-boost-program',
    title: 'Learning Boost Program',
    tagline: 'GES-aligned academic support & remedial learning for Ghanaian learners.',
    image: 'https://images.unsplash.com/photo-1497486751825-1233686d5d80?auto=format&fit=crop&w=1600&q=70',
    icon: 'Sparkles',
    gradient: 'from-yellow-400 to-amber-600',
    duration: 'Rolling (term-based)',
    level: 'Primary → Senior High',
    audience: 'Ghanaian students and their parents',
    summary:
      'A dedicated academic support and remedial learning programme aligned with the Ghana Education Service (GES) curriculum, designed to help learners close gaps and excel.',
    whatYouLearn: [
      'Targeted remediation in Math, English and Science',
      'Exam strategy for BECE and WASSCE',
      'Study skills, time-management and note-taking',
      'Digital literacy tied to school coursework',
      'Family progress reports every 4 weeks',
    ],
    outcomes: [
      'Measurable test-score improvement in target subjects',
      'Stronger study habits verified by parents and teachers',
    ],
    syllabus: [
      { week: 'Term 1', title: 'Diagnostic & Plan',      body: 'Assess gaps, set goals, buddy up.' },
      { week: 'Term 2', title: 'Targeted Remediation',   body: 'Weekly drills + mentor check-ins.' },
      { week: 'Term 3', title: 'Exam Sprint',             body: 'Past-paper clinics + mock exams.' },
    ],
  },
];

export const PROGRAM_BY_SLUG = Object.fromEntries(PROGRAMS.map((p) => [p.slug, p]));
